import { useState } from 'react';
import { signInWithEmailAndPassword, updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import Modal from './Modal';
import { ADMIN_COLORS } from '../constants/bookingConst';
import '../css/AdminUser.css';

// Shared jassen light palette (constants/bookingConst.js)
const COLORS = ADMIN_COLORS;

// Password strength checker
const checkPasswordStrength = (password) => {
  let score = 0;
  const feedback = [];

  if (password.length >= 8) {
    score += 1;
  } else if (password.length >= 6) {
    feedback.push('At least 8 characters');
  } else {
    feedback.push('Minimum 6 characters required');
  }

  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add lowercase letters');
  }

  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add uppercase letters');
  }

  if (/[0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add numbers');
  }

  if (/[^a-zA-Z0-9]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Add special characters (!@#$%^&*)');
  }

  let strength = 'weak';
  let color = COLORS.danger;

  if (score >= 4) {
    strength = 'strong';
    color = COLORS.success;
  } else if (score >= 2) {
    strength = 'medium';
    color = COLORS.warning;
  }

  return { strength, color, score, feedback: feedback.slice(0, 2) };
};

function AdminUser({ adminEmail }) {
  const [email, setEmail] = useState(() => {
    return auth.currentUser?.email || adminEmail || '';
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [modalConfig, setModalConfig] = useState({
    isOpen: false, title: '', message: '', type: 'info', confirmText: 'OK', onConfirm: null
  });

  const passwordStrength = newPassword ? checkPasswordStrength(newPassword) : null;

  const showModal = (config) => setModalConfig({ isOpen: true, ...config });
  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  const validate = () => {
    const newErrors = {};
    const currentUserEmail = auth.currentUser?.email || '';

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (email.toLowerCase() !== currentUserEmail.toLowerCase()) {
      newErrors.email = 'Email must match your current account';
    }

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    } else if (passwordStrength && passwordStrength.strength === 'weak') {
      newErrors.newPassword = 'Password is too weak. ' + passwordStrength.feedback.join(', ');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email matches current user
    const currentUserEmail = auth.currentUser?.email || '';
    if (email.toLowerCase() !== currentUserEmail.toLowerCase()) {
      showModal({
        title: 'Validation Error',
        message: 'The email must match your current logged in account.',
        type: 'error',
        confirmText: 'OK'
      });
      return;
    }

    if (!validate()) return;

    setLoading(true);
    try {
      let user = auth.currentUser;

      if (!user) {
        // Try to sign in with raw password to verify
        try {
          await signInWithEmailAndPassword(auth, email, currentPassword);
        } catch (signInError) {
          showModal({
            title: 'Authentication Failed',
            message: 'Your current password is incorrect. Please try again.',
            type: 'error',
            confirmText: 'OK'
          });
          setLoading(false);
          return;
        }
      }

      // Get the updated user after potential sign-in
      user = auth.currentUser;

      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Use raw password - Firebase Auth will hash it internally
      await updatePassword(user, newPassword);

      showModal({
        title: 'Success',
        message: 'Your password has been updated. Please use your new password when logging in next time.',
        type: 'success',
        confirmText: 'OK',
        onConfirm: () => {
          closeModal();
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }
      });

    } catch (error) {
      console.error('Error updating password:', error);

      let errorMessage = 'Failed to update password. Please try again.';

      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'For security reasons, please log out and log in again before changing your password.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Your current password is incorrect.';
      }

      showModal({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        confirmText: 'OK'
      });
    }
    setLoading(false);
  };

  return (
    <div className="admin-user" style={{ color: COLORS.textSecondary }}>
      <h2 className="section-title">👤 User Profile</h2>
      <p style={{ marginBottom: '1.5rem', opacity: 0.7 }}>
        Manage your admin account settings.
      </p>

      {/* Change Password Form */}
      <div
        className="password-section"
        style={{
          background: COLORS.cardSolid,
          padding: '1.5rem',
          borderRadius: '12px',
          border: `1px solid ${COLORS.line}`,
          maxWidth: '500px',
        }}
      >
        <h3 style={{ color: COLORS.ink, marginTop: 0, marginBottom: '1.5rem' }}>Change Password</h3>
        <form onSubmit={handleSubmit}>
          {/* Email Field */}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          {/* Current Password */}
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              className={errors.currentPassword ? 'error' : ''}
            />
            {errors.currentPassword && (
              <span className="error-message">{errors.currentPassword}</span>
            )}
          </div>

          {/* New Password */}
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className={errors.newPassword ? 'error' : ''}
            />

            {/* Password Strength Indicator */}
            {newPassword && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', color: passwordStrength?.color }}>
                    {passwordStrength.strength === 'strong' && '✓ Strong'}
                    {passwordStrength.strength === 'medium' && '⚠ Medium'}
                    {passwordStrength.strength === 'weak' && '✕ Weak'}
                  </span>
                </div>
                {/* Strength Bar */}
                <div style={{
                  height: '4px',
                  background: COLORS.line,
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    background: passwordStrength.color,
                    transition: 'all 0.3s ease',
                    borderRadius: '2px'
                  }} />
                </div>
                {/* Strength Requirements */}
                <div style={{
                  marginTop: '0.5rem',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.25rem',
                  fontSize: '0.7rem'
                }}>
                  <span style={{ color: passwordStrength?.score >= 1 ? COLORS.success : COLORS.textSecondary }}>
                    {passwordStrength?.score >= 1 ? '✓' : '○'} 8+ chars
                  </span>
                  <span style={{ color: passwordStrength?.score >= 2 ? COLORS.success : COLORS.textSecondary }}>
                    {passwordStrength?.score >= 2 ? '✓' : '○'} lowercase
                  </span>
                  <span style={{ color: passwordStrength?.score >= 3 ? COLORS.success : COLORS.textSecondary }}>
                    {passwordStrength?.score >= 3 ? '✓' : '○'} uppercase
                  </span>
                  <span style={{ color: passwordStrength?.score >= 4 ? COLORS.success : COLORS.textSecondary }}>
                    {passwordStrength?.score >= 4 ? '✓' : '○'} number
                  </span>
                </div>
              </div>
            )}
            {errors.newPassword && (
              <span className="error-message">{errors.newPassword}</span>
            )}
          </div>

          {/* Confirm New Password */}
          <div className="form-group">
            <label>Retype New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={errors.confirmPassword ? 'error' : ''}
            />
            {confirmPassword && newPassword === confirmPassword && (
              <span style={{ fontSize: '0.75rem', color: COLORS.success, marginTop: '0.25rem', display: 'block' }}>
                ✓ Passwords match
              </span>
            )}
            {errors.confirmPassword && (
              <span className="error-message">{errors.confirmPassword}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '1rem 2rem',
              background: COLORS.neonLime,
              color: COLORS.deepBg,
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              opacity: loading ? 0.7 : 1,
              width: '100%',
              marginTop: '0.5rem',
            }}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        confirmText={modalConfig.confirmText}
        onConfirm={modalConfig.onConfirm}
      />
    </div>
  );
}

export default AdminUser;
